/**
 * Ivy Chat API client.
 *
 * READS (session list, messages, CRUD) → Main API Server (always running, DB-backed)
 * WRITES (send message to Claude) → Fargate ivy-chat backend (on-demand)
 */

import { getCurrentUserToken } from '@/services/firebase';
import { getImpersonationUserId } from '@/stores/impersonationStore';

// Main API server — always running, DB-backed
function getApiBase(): string {
  return (window as any).__ENV__?.API_URL || import.meta.env.VITE_API_URL || '';
}

// Fargate backend — only for running Claude
function getChatBase(): string {
  return `${getApiBase()}/ivy-chat`;
}

const SESSION_TOKEN_KEY = 'ivy_chat_session_token';

export interface ChatSession {
  chatId: string;
  userId: string;
  claudeSessionId: string | null;
  conversationId: string | null;
  title: string;
  createdAt: string;
  lastActivityAt: string;
  messageCount: number;
  totalCostUsd: number;
  qualityTier?: string;
  status: 'idle' | 'busy';
  /** A publish/export asked for a learn turn and the user has said something since the last one. */
  learnPending?: boolean;
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  role: string;
  content: string;
  toolName: string | null;
  toolInput: any;
  toolUseId: string | null;
  isError: boolean;
  costUsd: number | null;
  durationMs: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  createdAt: string;
}

export type TurnKind = 'normal' | 'learn';

export interface StreamStatus {
  active: boolean;
  eventCount: number;
  startedAt?: string;
  turnKind?: TurnKind;
  requestId?: string;
}

// ── Auth helpers ──────────────────────────────────────────────────────────

/** Firebase token + impersonation header for main API server (reads) */
async function getFirebaseHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  let token = await getCurrentUserToken();
  if (!token) {
    await new Promise(r => setTimeout(r, 500));
    token = await getCurrentUserToken();
  }
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const impersonateUserId = getImpersonationUserId();
  if (impersonateUserId) headers['X-Impersonate-User'] = impersonateUserId;
  return headers;
}

/** ivy-chat session token for Fargate (writes) */
function getIvyChatHeaders(): Record<string, string> {
  const token = localStorage.getItem(SESSION_TOKEN_KEY);
  if (token) return { Authorization: `Bearer ${token}` };
  return {};
}

export function hasSessionToken(): boolean {
  return !!localStorage.getItem(SESSION_TOKEN_KEY);
}

export function setSessionToken(token: string): void {
  localStorage.setItem(SESSION_TOKEN_KEY, token);
}

export function clearSessionToken(): void {
  localStorage.removeItem(SESSION_TOKEN_KEY);
}

// ── Fargate auth (Firebase → ivy-chat session token exchange) ─────────

export async function exchangeFirebaseToken(
  firebaseToken: string, email: string, displayName: string,
): Promise<{ sessionToken: string; user: { userId: string; email: string; displayName: string } }> {
  const res = await fetch(`${getChatBase()}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firebaseToken, email, displayName }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Login failed (${res.status})`);
  }
  return res.json();
}

export async function validateSession(): Promise<{ user: { userId: string; email: string; displayName: string } } | null> {
  const headers = getIvyChatHeaders();
  if (!headers.Authorization) return null;
  try {
    const res = await fetch(`${getChatBase()}/api/auth/me`, { headers });
    if (res.ok) return res.json();
  } catch { /* invalid */ }
  return null;
}

// ── READS — Main API Server (DB-backed, always available) ─────────────

export async function listSessions(): Promise<ChatSession[]> {
  const headers = await getFirebaseHeaders();
  const res = await fetch(`${getApiBase()}/api/chat/sessions`, { headers });
  if (!res.ok) return [];
  const data = await res.json();
  return data.sessions;
}

export async function createSession(title?: string, conversationId?: string, qualityTier?: string): Promise<ChatSession> {
  const headers = await getFirebaseHeaders();
  const res = await fetch(`${getApiBase()}/api/chat/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ title, ...(conversationId ? { conversationId } : {}), ...(qualityTier ? { qualityTier } : {}) }),
  });
  if (!res.ok) throw new Error(`Failed to create session: ${res.status}`);
  return res.json();
}

export async function getSessionByConversation(conversationId: string): Promise<ChatSession | null> {
  const headers = await getFirebaseHeaders();
  const res = await fetch(`${getApiBase()}/api/chat/sessions/by-conversation/${conversationId}`, { headers });
  if (res.status === 404) return null;
  if (!res.ok) return null;
  return res.json();
}

export async function deleteSession(chatId: string): Promise<void> {
  const headers = await getFirebaseHeaders();
  await fetch(`${getApiBase()}/api/chat/sessions/${chatId}`, {
    method: 'DELETE',
    headers,
  });
}

export async function getMessages(chatId: string): Promise<ChatMessage[]> {
  const headers = await getFirebaseHeaders();
  const res = await fetch(`${getApiBase()}/api/chat/sessions/${chatId}/messages`, { headers });
  if (!res.ok) return [];
  const data = await res.json();
  return data.messages;
}

// ── WRITES — Fargate (only when chatting) ─────────────────────────────

/**
 * Send a chat message to Claude via Fargate.
 * Returns fetch Response for NDJSON streaming.
 */
export function sendChatMessage(chatId: string, message: string, requestId: string, qualityTier?: string, signal?: AbortSignal): Promise<Response> {
  // Token is now read from encrypted DB on the backend — no localStorage needed
  return fetch(`${getChatBase()}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getIvyChatHeaders() },
    body: JSON.stringify({ chatId, message, requestId, ...(qualityTier ? { qualityTier } : {}) }),
    signal,
  });
}

/** Start a learn-from-session turn. 409 when the chat is busy or nothing is pending. */
export function sendLearnTurn(chatId: string, requestId: string): Promise<Response> {
  return fetch(`${getChatBase()}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getIvyChatHeaders() },
    body: JSON.stringify({ chatId, requestId, turnKind: 'learn' }),
  });
}

export async function abortRequest(requestId: string): Promise<void> {
  try {
    const res = await fetch(`${getChatBase()}/api/abort/${requestId}`, {
      method: 'POST',
      headers: getIvyChatHeaders(),
    });
    if (!res.ok) {
      console.warn(`[ivyChatApi] abort failed: ${res.status}`, await res.text().catch(() => ''));
    }
  } catch (err) {
    console.warn('[ivyChatApi] abort request error:', err);
  }
}

export async function getStreamStatus(chatId: string): Promise<StreamStatus> {
  const res = await fetch(`${getChatBase()}/api/sessions/${chatId}/stream-status`, {
    headers: getIvyChatHeaders(),
  });
  if (!res.ok) return { active: false, eventCount: 0 };
  return res.json();
}

export function reconnectToStream(chatId: string, sinceIndex: number): Promise<Response> {
  return fetch(`${getChatBase()}/api/sessions/${chatId}/reconnect?since=${sinceIndex}`, {
    headers: getIvyChatHeaders(),
  });
}

// ── User Secrets (encrypted Claude token) ────────────────────────────

export async function getClaudeTokenStatus(): Promise<{ hasToken: boolean; hint: string | null }> {
  const headers = await getFirebaseHeaders();
  const res = await fetch(`${getApiBase()}/api/user/claude-token/status`, { headers });
  if (!res.ok) return { hasToken: false, hint: null };
  return res.json();
}

export async function saveClaudeToken(token: string): Promise<{ success: boolean; hint?: string; error?: string }> {
  const headers = await getFirebaseHeaders();
  const res = await fetch(`${getApiBase()}/api/user/claude-token`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ token }),
  });
  return res.json();
}

export async function deleteClaudeToken(): Promise<void> {
  const headers = await getFirebaseHeaders();
  await fetch(`${getApiBase()}/api/user/claude-token`, {
    method: 'DELETE',
    headers,
  });
}

// ── Multi-Provider Auth ─────────────────────────────────────────────

export type ClaudeProvider = 'claude_token' | 'anthropic' | 'bedrock';

export interface ProviderInfo {
  hasKey: boolean;
  hint: string | null;
  isValid?: boolean | null;
}

export interface ProviderStatus {
  activeProvider: ClaudeProvider;
  providers: Record<ClaudeProvider, ProviderInfo>;
  hasAnyKey: boolean;
}

export async function getProviderStatus(): Promise<ProviderStatus> {
  const headers = await getFirebaseHeaders();
  const res = await fetch(`${getApiBase()}/api/user/claude-provider/status`, { headers });
  if (!res.ok) {
    return {
      activeProvider: 'claude_token',
      providers: {
        claude_token: { hasKey: false, hint: null },
        anthropic: { hasKey: false, hint: null },
        bedrock: { hasKey: false, hint: null },
      },
      hasAnyKey: false,
    };
  }
  return res.json();
}

/**
 * Whether the platform funds runs for users who have no credential of their own
 * (hosted mode, PRD B2). When CREDENTIAL_SOURCE=platform the server leases from the
 * account pool, so the chat must not demand a key before it will send. The server's
 * answer is read once and cached. A failure answers false, which keeps the key-required
 * behaviour, and is not cached, so a 502 during a deploy is asked again on the next check.
 */
let platformFundedCache: Promise<boolean> | null = null;
export function isPlatformFunded(): Promise<boolean> {
  if (!platformFundedCache) {
    platformFundedCache = (async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/health`);
        if (!res.ok) throw new Error(`health ${res.status}`);
        const body = await res.json();
        return body?.flags?.CREDENTIAL_SOURCE === 'platform';
      } catch {
        platformFundedCache = null;
        return false;
      }
    })();
  }
  return platformFundedCache;
}

export async function setActiveProvider(provider: ClaudeProvider): Promise<{ success: boolean }> {
  const headers = await getFirebaseHeaders();
  const res = await fetch(`${getApiBase()}/api/user/claude-provider`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ provider }),
  });
  return res.json();
}

export interface ApiKeyEntry {
  id: string;
  provider: string;
  keyPrefix: string;
  label: string | null;
  isValid: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export async function listApiKeys(): Promise<ApiKeyEntry[]> {
  const headers = await getFirebaseHeaders();
  const res = await fetch(`${getApiBase()}/api/keys`, { headers });
  if (!res.ok) return [];
  return res.json();
}

export async function saveApiKey(
  provider: 'ANTHROPIC' | 'BEDROCK' | 'VERTEX',
  key: string,
  label?: string,
): Promise<{ id: string; provider: string; keyPrefix: string } | { error: string }> {
  const headers = await getFirebaseHeaders();
  const res = await fetch(`${getApiBase()}/api/keys`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ provider, key, label }),
  });
  return res.json();
}

export async function deleteApiKey(id: string): Promise<void> {
  const headers = await getFirebaseHeaders();
  await fetch(`${getApiBase()}/api/keys/${id}`, {
    method: 'DELETE',
    headers,
  });
}

export async function validateApiKey(id: string): Promise<{ isValid: boolean; error?: string }> {
  const headers = await getFirebaseHeaders();
  const res = await fetch(`${getApiBase()}/api/keys/${id}/validate`, {
    method: 'POST',
    headers,
  });
  return res.json();
}
